import React, { useState, useEffect, useCallback } from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import apiClient from '../api/client';

interface SessionStats {
  session_id: string | null;
  sedentary_time_minutes: number;
  avg_posture_score: number | null;
  movement_events: number;
  break_count: number;
  status: string;
  start_time: string | null;
}

interface AlertsByType {
  break_reminder: number;
  posture_correction: number;
  hydration_reminder: number;
  cold_detection: number;
}

interface DashboardData {
  employee_id: string;
  employee_name: string;
  current_session: SessionStats;
  today_stats: {
    total_sedentary_minutes: number;
    avg_posture_score: number | null;
    total_movement_events: number;
    alerts_received: number;
    sessions_count: number;
  };
  alerts_by_type: AlertsByType;
  posture_trend?: PostureTrend[];
}

interface PostureTrend {
  timestamp: string;
  score: number;
}

export function EmployeeDashboard() {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [postureTrend, setPostureTrend] = useState<PostureTrend[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());

  const fetchDashboardData = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/employee/dashboard');
      setDashboardData(response.data);
      if (response.data.posture_trend) {
        setPostureTrend(response.data.posture_trend);
      }
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatDuration = (minutes: number): string => {
    if (minutes == null || isNaN(minutes)) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const getPostureStatus = (score: number | null) => {
    if (score === null) return { label: 'No Data', color: '#9CA3AF', emoji: '❓' };
    if (score >= 80) return { label: 'Excellent', color: '#10B981', emoji: '🌟' };
    if (score >= 65) return { label: 'Good', color: '#34D399', emoji: '👍' };
    if (score >= 50) return { label: 'Fair', color: '#FBBF24', emoji: '⚡' };
    return { label: 'Needs Work', color: '#EF4444', emoji: '💪' };
  };

  const getSessionStatus = (status: string) => {
    switch (status) {
      case 'active': return { label: 'Active', color: '#10B981', bg: '#D1FAE5' };
      case 'on_break': return { label: 'On Break', color: '#F59E0B', bg: '#FEF3C7' };
      default: return { label: 'No Session', color: '#6B7280', bg: '#F3F4F6' };
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner}>
          <div style={styles.spinnerRing} />
        </div>
        <p style={styles.loadingText}>Loading your wellness data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <span style={styles.errorIcon}>😔</span>
        <h3>Oops! Something went wrong</h3>
        <p>{error}</p>
        <button onClick={fetchDashboardData} style={styles.retryBtn}>Try Again</button>
      </div>
    );
  }

  const postureStatus = getPostureStatus(dashboardData?.current_session.avg_posture_score ?? null);
  const sessionStatus = getSessionStatus(dashboardData?.current_session.status || '');

  return (
    <div style={styles.container}>
      {/* Welcome Header */}
      <div style={styles.welcomeSection}>
        <div style={styles.welcomeContent}>
          <h1 style={styles.welcomeTitle}>
            Welcome back, {dashboardData?.employee_name?.split(' ')[0]} 👋
          </h1>
          <p style={styles.welcomeSubtitle}>
            {currentTime.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            {' • '}
            {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        <div style={styles.sessionBadge}>
          <div style={{
            ...styles.sessionDot,
            background: sessionStatus.color,
            boxShadow: `0 0 12px ${sessionStatus.color}`,
          }} />
          <span style={{ color: sessionStatus.color, fontWeight: 600 }}>{sessionStatus.label}</span>
        </div>
      </div>

      {/* Main Stats Grid */}
      <div style={styles.statsGrid}>
        {/* Posture Score - Hero Card */}
        <div style={styles.heroCard}>
          <div style={styles.heroCardInner}>
            <div style={styles.heroCardContent}>
              <span style={styles.heroEmoji}>{postureStatus.emoji}</span>
              <div style={styles.heroScoreContainer}>
                <span style={styles.heroScore}>
                  {dashboardData?.current_session.avg_posture_score?.toFixed(0) ?? '--'}
                </span>
                <span style={styles.heroScoreMax}>/100</span>
              </div>
              <span style={{...styles.heroLabel, color: 'white'}}>
                {postureStatus.label}
              </span>
              <span style={styles.heroTitle}>Posture Score</span>
            </div>
            <div style={styles.heroRing}>
              <svg viewBox="0 0 100 100" style={styles.heroRingSvg}>
                <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="8" />
                <circle 
                  cx="50" cy="50" r="45" fill="none" 
                  stroke="white" strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${(dashboardData?.current_session.avg_posture_score ?? 0) * 2.83} 283`}
                  transform="rotate(-90 50 50)"
                  style={{ transition: 'stroke-dasharray 1s ease-out' }}
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Sedentary Time */}
        <div style={styles.statCard}>
          <div style={styles.statCardIcon}>
            <span>⏱️</span>
          </div>
          <div style={styles.statCardContent}>
            <span style={styles.statCardValue}>
              {formatDuration(dashboardData?.current_session.sedentary_time_minutes ?? 0)}
            </span>
            <span style={styles.statCardLabel}>Sedentary Time</span>
          </div>
        </div>

        {/* Breaks Taken */}
        <div style={styles.statCard}>
          <div style={{...styles.statCardIcon, background: 'linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)'}}>
            <span>🚶</span>
          </div>
          <div style={styles.statCardContent}>
            <span style={styles.statCardValue}>
              {dashboardData?.current_session.break_count ?? 0}
            </span>
            <span style={styles.statCardLabel}>Breaks Taken</span>
          </div>
        </div>

        {/* Movement Events */}
        <div style={styles.statCard}>
          <div style={{...styles.statCardIcon, background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)'}}>
            <span>💫</span>
          </div>
          <div style={styles.statCardContent}>
            <span style={styles.statCardValue}>
              {dashboardData?.current_session.movement_events ?? 0}
            </span>
            <span style={styles.statCardLabel}>Movements</span>
          </div>
        </div>
      </div>

      {/* Wellness Nudges Section */}
      <div style={styles.nudgesSection}>
        <h2 style={styles.sectionTitle}>
          <img src="/logo.png" alt="" style={styles.sectionLogo} /> Today's Wellness Nudges
        </h2>
        <div style={styles.nudgesGrid}>
          <div style={{...styles.nudgeCard, background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)'}}>
            <span style={styles.nudgeIcon}>⏰</span>
            <span style={{...styles.nudgeCount, color: '#F59E0B'}}>{dashboardData?.alerts_by_type?.break_reminder ?? 0}</span>
            <span style={styles.nudgeLabel}>Break Reminders</span>
          </div>
          <div style={{...styles.nudgeCard, background: 'linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)'}}>
            <span style={styles.nudgeIcon}>🧘</span>
            <span style={{...styles.nudgeCount, color: '#3B82F6'}}>{dashboardData?.alerts_by_type?.posture_correction ?? 0}</span>
            <span style={styles.nudgeLabel}>Posture Tips</span>
          </div>
          <div style={{...styles.nudgeCard, background: 'linear-gradient(135deg, #CFFAFE 0%, #A5F3FC 100%)'}}>
            <span style={styles.nudgeIcon}>💧</span>
            <span style={{...styles.nudgeCount, color: '#06B6D4'}}>{dashboardData?.alerts_by_type?.hydration_reminder ?? 0}</span>
            <span style={styles.nudgeLabel}>Hydration</span>
          </div>
          <div style={{...styles.nudgeCard, background: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)'}}>
            <span style={styles.nudgeIcon}>🧣</span>
            <span style={{...styles.nudgeCount, color: '#8B5CF6'}}>{dashboardData?.alerts_by_type?.cold_detection ?? 0}</span>
            <span style={styles.nudgeLabel}>Comfort Alerts</span>
          </div>
        </div>
      </div>

      {/* Posture Trend Chart */}
      <div style={styles.chartSection}>
        <h2 style={styles.sectionTitle}>
          <span>📈</span> Posture Trend
        </h2>
        <div style={styles.chartCard}>
          {postureTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={postureTrend}>
                <defs>
                  <linearGradient id="postureGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  dataKey="timestamp" 
                  tickFormatter={(val) => new Date(val).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  stroke="#9CA3AF"
                  fontSize={12}
                />
                <YAxis domain={[0, 100]} stroke="#9CA3AF" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    background: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                  }}
                  labelFormatter={(val) => new Date(val).toLocaleString()}
                  formatter={(val: number) => [`${val.toFixed(0)}`, 'Score']}
                />
                <Area
                  type="monotone"
                  dataKey="score"
                  stroke="#10B981"
                  strokeWidth={3}
                  fill="url(#postureGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={styles.noDataContainer}>
              <span style={styles.noDataIcon}>📊</span>
              <p style={styles.noDataText}>No posture data available yet</p>
              <p style={styles.noDataHint}>Start a session to see your trends</p>
            </div>
          )}
        </div>
      </div>

      {/* Motivational Footer */}
      <div style={styles.motivationalCard}>
        <span style={styles.motivationalEmoji}>💚</span>
        <p style={styles.motivationalText}>
          Remember: Every small movement counts! Take a moment to stretch and breathe.
        </p>
      </div>
    </div>
  );
}


const styles: { [key: string]: React.CSSProperties } = {
  container: { maxWidth: '1200px', margin: '0 auto' },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    gap: '20px',
  },
  loadingSpinner: { width: '60px', height: '60px', position: 'relative' },
  spinnerRing: {
    width: '100%',
    height: '100%',
    border: '4px solid #E5E7EB',
    borderTopColor: '#10B981',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: { color: '#6B7280', fontSize: '16px' },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    gap: '16px',
    textAlign: 'center',
  },
  errorIcon: { fontSize: '64px' },
  retryBtn: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  welcomeSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
  },
  welcomeContent: {},
  welcomeTitle: { fontSize: '32px', fontWeight: 700, color: '#064E3B', margin: 0 },
  welcomeSubtitle: { fontSize: '15px', color: '#6B7280', marginTop: '6px' },
  sessionBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 20px',
    background: 'white',
    borderRadius: '14px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  },
  sessionDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    animation: 'pulse 2s ease-in-out infinite',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '20px',
    marginBottom: '32px',
  },
  heroCard: {
    background: 'linear-gradient(135deg, #10B981 0%, #059669 50%, #047857 100%)',
    borderRadius: '24px',
    padding: '24px',
    color: 'white',
    boxShadow: '0 10px 40px rgba(16, 185, 129, 0.3)',
  },
  heroCardInner: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '100%',
  },
  heroCardContent: { display: 'flex', flexDirection: 'column', gap: '4px' },
  heroEmoji: { fontSize: '28px' },
  heroScoreContainer: { display: 'flex', alignItems: 'baseline', gap: '4px' },
  heroScore: { fontSize: '48px', fontWeight: 800, lineHeight: 1 },
  heroScoreMax: { fontSize: '18px', opacity: 0.7 },
  heroLabel: { fontSize: '14px', fontWeight: 600, marginTop: '4px' },
  heroTitle: { fontSize: '13px', opacity: 0.8, marginTop: '8px' },
  heroRing: { width: '80px', height: '80px' },
  heroRingSvg: { width: '100%', height: '100%' },
  statCard: {
    background: 'white',
    borderRadius: '20px',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
  },
  statCardIcon: {
    width: '52px',
    height: '52px',
    borderRadius: '16px',
    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    boxShadow: '0 8px 20px rgba(16, 185, 129, 0.25)',
  },
  statCardContent: { display: 'flex', flexDirection: 'column' },
  statCardValue: { fontSize: '32px', fontWeight: 700, color: '#1F2937', lineHeight: 1 },
  statCardLabel: { fontSize: '14px', fontWeight: 600, color: '#4B5563', marginTop: '6px' },
  nudgesSection: { marginBottom: '32px' },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '20px',
    fontWeight: 600,
    color: '#1F2937',
    marginBottom: '20px',
  },
  sectionLogo: {
    width: '24px',
    height: '24px',
    objectFit: 'contain',
  },
  nudgesGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' },
  nudgeCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '24px 16px',
    borderRadius: '20px',
  },
  nudgeIcon: { fontSize: '32px', marginBottom: '12px' },
  nudgeCount: { fontSize: '36px', fontWeight: 700, lineHeight: 1 },
  nudgeLabel: { fontSize: '13px', color: '#4B5563', marginTop: '8px', textAlign: 'center', fontWeight: 500 },
  chartSection: { marginBottom: '32px' },
  chartCard: {
    background: 'white',
    borderRadius: '24px',
    padding: '24px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
  },
  noDataContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    color: '#6B7280',
  },
  noDataIcon: { fontSize: '48px', marginBottom: '16px', opacity: 0.5 },
  noDataText: { fontSize: '16px', fontWeight: 500 },
  noDataHint: { fontSize: '14px', color: '#9CA3AF', marginTop: '4px' },
  motivationalCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '20px 24px',
    background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)',
    borderRadius: '16px',
    border: '1px solid #A7F3D0',
  },
  motivationalEmoji: { fontSize: '28px' },
  motivationalText: { fontSize: '15px', color: '#065F46', fontWeight: 500, margin: 0 },
};
